import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Alert, Box, Paper, Typography } from '@mui/material'
import { ThemeProvider } from '@mui/material/styles'
import {
  DndContext,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import API from '../../../services/API'
import { buildWallboardTheme } from '../wallboardTheme'
import { WIDGET_REGISTRY } from '../widgets/registry'
import {
  layoutScreen,
  spanOf,
  rectsOverlap,
  GRID_COLS,
  GRID_ROWS,
  MIN_COLS,
  MIN_ROWS,
} from '../layout'

// The canvas renders the REAL widgets at full TV resolution on a virtual
// 1920×1080 stage, then shrinks the whole stage with transform: scale() to
// fit the editor. That keeps every proportion identical to the display —
// nothing truncates or reflows differently at editor size, it's simply
// zoomed out. Drag and resize pointer deltas are divided by the scale so the
// widgets track the cursor exactly.
const VIRTUAL_W = 1920
const VIRTUAL_H = 1080
const GRID_GAP = 12
const CELL_W = Math.floor((VIRTUAL_W - GRID_GAP * (GRID_COLS - 1)) / GRID_COLS)
const CELL_H = Math.floor((VIRTUAL_H - GRID_GAP * (GRID_ROWS - 1)) / GRID_ROWS)

const clamp = (value, min, max) => Math.min(Math.max(value, min), max)

const DropCell = ({ col, row }) => {
  const { setNodeRef, isOver } = useDroppable({ id: `cell-${col}-${row}` })
  return (
    <Box
      ref={setNodeRef}
      sx={{
        gridColumn: `${col + 1} / span 1`,
        gridRow: `${row + 1} / span 1`,
        border: '2px dashed',
        borderColor: isOver ? 'primary.main' : 'rgba(148, 163, 184, 0.15)',
        borderRadius: 2,
        bgcolor: isOver ? 'rgba(79, 179, 191, 0.14)' : 'transparent',
        transition: 'background-color 0.1s, border-color 0.1s',
      }}
    />
  )
}

const DraggableWidget = ({
  uid,
  index,
  placement,
  heightPx,
  data,
  scale,
  onResizeStart,
  resizing,
}) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useDraggable({ id: uid, data: { index }, disabled: resizing })
  const definition = WIDGET_REGISTRY[placement.widget.type]
  if (!definition) return null
  const WidgetComponent = definition.component

  return (
    <Box
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      sx={{
        gridColumn: `${placement.col + 1} / span ${placement.cols}`,
        gridRow: `${placement.row + 1} / span ${placement.rows}`,
        height: heightPx,
        minWidth: 0,
        overflow: 'hidden',
        cursor: 'grab',
        // Pointer deltas are screen pixels; the stage is scaled, so divide
        // to keep the dragged widget under the cursor
        transform: transform
          ? `translate3d(${transform.x / scale}px, ${transform.y / scale}px, 0)`
          : undefined,
        zIndex: isDragging || resizing ? 10 : 1,
        opacity: isDragging ? 0.85 : 1,
        position: 'relative',
        borderRadius: 1,
        outline: resizing ? '3px solid' : 'none',
        outlineColor: 'primary.main',
        '&:active': { cursor: 'grabbing' },
        '&:hover .wallboard-resize-handle': { opacity: 1 },
      }}
    >
      {/* The real widget, display-only — the wrapper takes the drag */}
      <Box sx={{ height: '100%', pointerEvents: 'none', userSelect: 'none' }}>
        <WidgetComponent widget={placement.widget} data={data} cellPx={heightPx} />
      </Box>
      {/* Corner resize handle — sized for the scaled-down stage */}
      <Box
        className="wallboard-resize-handle"
        onPointerDown={(event) => onResizeStart(index, event)}
        sx={{
          position: 'absolute',
          right: 4,
          bottom: 4,
          width: 36,
          height: 36,
          cursor: 'nwse-resize',
          opacity: resizing ? 1 : 0,
          transition: 'opacity 0.15s',
          borderRight: '6px solid',
          borderBottom: '6px solid',
          borderColor: 'primary.main',
          borderBottomRightRadius: 10,
        }}
      />
    </Box>
  )
}

// The editor's layout canvas: a scaled-down 1920×1080 stage showing the real
// widgets with live data. Placement and span are written to each widget
// (col/row/cols/rows) — the display uses the identical layoutScreen(), so
// what you arrange here is exactly what the TV shows.
const LayoutCanvas = ({ boardId, screen, updateScreen, themeMode = 'dark' }) => {
  const [data, setData] = useState(null)
  const wrapperRef = useRef(null)
  const [scale, setScale] = useState(0.5)
  const scaleRef = useRef(0.5)
  // Live resize preview: { index, col, row, cols, rows }
  const [resize, setResize] = useState(null)
  const resizeRef = useRef(null)

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      const res = await API.get('/wallboard/data', {
        params: { wallboardId: boardId },
        skipGlobalErrorHandler: true,
      })
      if (!cancelled && res.status === 200) setData(res.data.data)
    }
    load()
    return () => {
      cancelled = true
    }
  }, [boardId])

  useEffect(() => {
    const element = wrapperRef.current
    if (!element) return undefined
    const measure = () => {
      const width = element.clientWidth
      if (width > 0) {
        const next = Math.min(width / VIRTUAL_W, 1)
        scaleRef.current = next
        setScale((current) => (Math.abs(current - next) < 0.001 ? current : next))
      }
    }
    const frame = requestAnimationFrame(measure)
    const observer = new ResizeObserver(measure)
    observer.observe(element)
    return () => {
      cancelAnimationFrame(frame)
      observer.disconnect()
    }
  }, [])

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  )

  const placements = layoutScreen(screen.widgets ?? [])
  const unplaced = placements.filter((placement) => placement.col === null)

  // Un-pins every OTHER pinned widget that the given rect now covers, so they
  // reflow first-fit instead of overlapping
  const withCollisionsCleared = (widgets, rect, keepIndex) =>
    widgets.map((candidate, i) => {
      if (i === keepIndex) return candidate
      if (
        Number.isInteger(candidate.col) &&
        Number.isInteger(candidate.row) &&
        rectsOverlap(rect, {
          col: candidate.col,
          row: candidate.row,
          ...spanOf(candidate),
        })
      ) {
        const { col: _col, row: _row, ...rest } = candidate
        return rest
      }
      return candidate
    })

  const onDragEnd = ({ active, over }) => {
    if (!over) return
    const [, colText, rowText] = String(over.id).split('-')
    const index = active.data.current?.index
    const widget = (screen.widgets ?? [])[index]
    if (widget === undefined) return

    const { cols, rows } = spanOf(widget)
    const col = clamp(Number(colText), 0, GRID_COLS - cols)
    const row = clamp(Number(rowText), 0, GRID_ROWS - rows)

    updateScreen((current) => ({
      ...current,
      widgets: withCollisionsCleared(
        current.widgets.map((candidate, i) =>
          i === index ? { ...candidate, col, row } : candidate,
        ),
        { col, row, cols, rows },
        index,
      ),
    }))
  }

  const onResizeStart = useCallback(
    (index, event) => {
      // Keep the drag sensor out of it — this pointer is a resize
      event.stopPropagation()
      event.preventDefault()

      const placement = layoutScreen(screen.widgets ?? []).find(
        (candidate) => candidate.index === index,
      )
      if (!placement || placement.col === null) return

      resizeRef.current = {
        index,
        startX: event.clientX,
        startY: event.clientY,
        start: placement,
      }
      setResize({
        index,
        col: placement.col,
        row: placement.row,
        cols: placement.cols,
        rows: placement.rows,
      })

      const onMove = (moveEvent) => {
        const state = resizeRef.current
        if (!state) return
        // Screen pixels → virtual stage pixels → whole cells
        const currentScale = scaleRef.current || 1
        const dCols = Math.round(
          (moveEvent.clientX - state.startX) / currentScale / (CELL_W + GRID_GAP),
        )
        const dRows = Math.round(
          (moveEvent.clientY - state.startY) / currentScale / (CELL_H + GRID_GAP),
        )
        const cols = clamp(
          state.start.cols + dCols,
          MIN_COLS,
          GRID_COLS - state.start.col,
        )
        const rows = clamp(
          state.start.rows + dRows,
          MIN_ROWS,
          GRID_ROWS - state.start.row,
        )
        setResize((current) =>
          current && (current.cols !== cols || current.rows !== rows)
            ? { ...current, cols, rows }
            : current,
        )
      }

      const onUp = () => {
        const state = resizeRef.current
        resizeRef.current = null
        window.removeEventListener('pointermove', onMove)
        window.removeEventListener('pointerup', onUp)

        setResize((finalResize) => {
          if (state && finalResize) {
            const rect = {
              col: finalResize.col,
              row: finalResize.row,
              cols: finalResize.cols,
              rows: finalResize.rows,
            }
            updateScreen((current) => ({
              ...current,
              widgets: withCollisionsCleared(
                current.widgets.map((candidate, i) =>
                  i === state.index
                    ? {
                        ...candidate,
                        col: rect.col,
                        row: rect.row,
                        cols: rect.cols,
                        rows: rect.rows,
                      }
                    : candidate,
                ),
                rect,
                state.index,
              ),
            }))
          }
          return null
        })
      }

      window.addEventListener('pointermove', onMove)
      window.addEventListener('pointerup', onUp)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [screen.widgets, updateScreen],
  )

  return (
    <Box>
      <Typography variant="caption" color="text.secondary">
        Layout — a scaled-down preview of the TV with live data. Drag widgets
        to place them; drag a corner to resize. Newly added widgets show data
        after saving.
      </Typography>
      <ThemeProvider theme={buildWallboardTheme(themeMode)}>
        {/* Wrapper reserves the scaled footprint; the stage inside renders at
            full 1920×1080 and shrinks via transform */}
        <Box
          ref={wrapperRef}
          sx={{
            mt: 0.5,
            width: '100%',
            height: VIRTUAL_H * scale,
            position: 'relative',
            overflow: 'hidden',
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          {/* Absolutely positioned so the 1920px stage never contributes
              intrinsic width to the page — in-flow it forces the whole app
              content area wide and the page scrolls horizontally */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: VIRTUAL_W,
              height: VIRTUAL_H,
              transform: `scale(${scale})`,
              transformOrigin: 'top left',
              bgcolor: 'background.default',
              p: `${GRID_GAP}px`,
              boxSizing: 'border-box',
            }}
          >
            <DndContext sensors={sensors} onDragEnd={onDragEnd}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: `repeat(${GRID_COLS}, ${CELL_W}px)`,
                  gridTemplateRows: `repeat(${GRID_ROWS}, ${CELL_H}px)`,
                  gap: `${GRID_GAP}px`,
                }}
              >
                {Array.from({ length: GRID_ROWS }).map((row, rowIndex) =>
                  Array.from({ length: GRID_COLS }).map((col, colIndex) => (
                    <DropCell
                      key={`cell-${colIndex}-${rowIndex}`}
                      col={colIndex}
                      row={rowIndex}
                    />
                  )),
                )}
                {placements.map((placement) => {
                  if (placement.col === null) return null
                  const isResizing = resize?.index === placement.index
                  const shown = isResizing
                    ? { ...placement, cols: resize.cols, rows: resize.rows }
                    : placement
                  return (
                    <DraggableWidget
                      key={
                        placement.widget._id ??
                        placement.widget._uid ??
                        placement.index
                      }
                      uid={
                        placement.widget._id ??
                        placement.widget._uid ??
                        `widget-${placement.index}`
                      }
                      index={placement.index}
                      placement={shown}
                      heightPx={
                        CELL_H * shown.rows + GRID_GAP * (shown.rows - 1)
                      }
                      data={data ?? {}}
                      scale={scale}
                      onResizeStart={onResizeStart}
                      resizing={isResizing}
                    />
                  )
                })}
              </Box>
            </DndContext>
          </Box>
        </Box>
      </ThemeProvider>
      {/* Below the canvas so appearing/disappearing never shifts the stage */}
      {unplaced.length > 0 && (
        <Alert severity="warning" sx={{ mt: 1, py: 0 }}>
          Not enough room for{' '}
          {unplaced
            .map(
              (placement) =>
                WIDGET_REGISTRY[placement.widget.type]?.label ??
                placement.widget.type,
            )
            .join(', ')}{' '}
          — resize or remove widgets, or move them to another screen.
        </Alert>
      )}
    </Box>
  )
}

export default LayoutCanvas
